import posthog from 'posthog-js'
import {
  EVENT_ACTIVITIES_REPORT_ACTIVITY_CATALOG,
  EVENT_ACTIVITIES_REPORT_APPLY_ACTIVITY,
  EVENT_ACTIVITIES_TAB_ALL_ACTIVITIES,
  EVENT_ACTIVITIES_TAB_ARVORE_ACTIVITIES,
  EVENT_ACTIVITIES_TAB_MY_ACTIVITIES,
  EVENT_ACTIVITY_CATALOG_TAB_ALL_ACTIVITIES,
  EVENT_ACTIVITY_CATALOG_TAB_ARVORE_ACTIVITIES,
  EVENT_ACTIVITY_CATALOG_TAB_MY_ACTIVITIES,
  EVENT_AI_SUGGESTION_ACCEPTED,
  EVENT_AI_SUGGESTION_COMMENT_EDIT_CLICKED,
  EVENT_AI_SUGGESTION_COMMENT_SAVED,
  EVENT_AI_SUGGESTION_IGNORED,
  EVENT_AI_SUGGESTION_STATUS_CLICKED,
  EVENT_AUTHOR_SHORTCUT,
  EVENT_BANNER_CLICK,
  EVENT_BOOK_CREATION_ADMIN_RESULTS_CLICKED,
  EVENT_BOOK_CREATION_BANNER_CLICKED,
  EVENT_BOOK_CREATION_BANNER_VIEWED,
  EVENT_BOOK_CREATION_MODAL_APPLY_CLICKED,
  EVENT_BOOK_INTERMEDIATE_APPLY_ACTIVITY,
  EVENT_BOOK_INTERMEDIATE_CREATE_ACTIVITY,
  EVENT_BOOK_INTERMEDIATE_TAB_ACTIVITIES,
  EVENT_CONTENT_OPENED,
  EVENT_CREATE_ACTIVITY_V3,
  EVENT_DOWNLOAD_RUNNING_RECORD_TUTORIAL,
  EVENT_INDICATIONS_REPORT_VIEW,
  EVENT_MODAL_CLICKED,
  EVENT_MODAL_CLOSED,
  EVENT_MODAL_IMPRESSIONS,
  EVENT_ONBOARDING_BOOK_CLICKED,
  EVENT_ONBOARDING_COMPLETED,
  EVENT_ONBOARDING_LIKED_BOOKS,
  EVENT_ONBOARDING_SELECTED_THEMES,
  EVENT_ONBOARDING_STARTED,
  EVENT_ONBOARDING_STEP_COMPLETED,
  EVENT_ONBOARDING_UNLIKED_BOOKS,
  EVENT_PQL_WRITE_CREATE_LIST_CLICKED,
  EVENT_PQL_WRITE_INDICATE_CLICKED,
  EVENT_PQL_WRITE_MODAL_VIEWED,
  EVENT_PQL_WRITE_READ_MYSELF_CLICKED,
  EVENT_PUBLISHER_SHORTCUT,
  EVENT_RELATED_CONTENT_TAB_CLICKED,
  EVENT_RUNNING_RECORD_CALCULATED,
  EVENT_RUNNING_RECORDS_BANNER_CLICKED,
  EVENT_RUNNING_RECORDS_CALCULATOR,
  EVENT_RUNNING_RECORDS_DOWNLOAD_SHEET,
  EVENT_RUNNING_RECORDS_EVALUATION_SELECTED,
  EVENT_RUNNING_RECORDS_EXPORT,
  EVENT_RUNNING_RECORDS_FILTER_CHANGED,
  EVENT_RUNNING_RECORDS_HISTORY,
  EVENT_RUNNING_RECORDS_LISTEN_RECORDINGS,
  EVENT_RUNNING_RECORDS_METHOD_DETAILS,
  EVENT_RUNNING_RECORDS_PLAY_RECORDING,
  EVENT_RUNNING_RECORDS_PROGRESS_SHEET,
  EVENT_RUNNING_RECORDS_REGISTER_ASSESSMENT,
  EVENT_RUNNING_RECORDS_SAVE_ASSESSMENT,
  EVENT_SEARCH,
  EVENT_SUPERAUTOR_SHELF_BOOK_CLICKED,
  EVENT_SUPERAUTOR_SHELF_CTA_CLICKED,
  EVENT_SUPERAUTOR_SHELF_VIEWED,
  EVENT_TIME_USAGE_REPORT_GUIDE_DOWNLOADED,
  EVENT_TIME_USAGE_REPORT_RUBRIC_DOWNLOADED,
  EVENT_VIEW_FULL_COLLECTION_SHORTCUT
} from './analytics.constants'

type Dict = Record<string, unknown>

export const trackEvent = (event: string, props?: Dict) => {
  posthog.capture(event, props)
}

export const userIdentify = (id: string) => {
  posthog.identify(id)
}

export const userReset = () => {
  posthog.reset()
}

export const registerSuperProperty = (props: Dict, days?: number) => {
  posthog.register(props, days)
}

export const userIncrement = (prop: string | Dict) => {
  if (typeof prop === 'string') {
    const payload = {
      $set: {
        $increment: {
          [prop]: 1
        }
      }
    }
    posthog.capture('$set', payload)
  } else if (typeof prop === 'object') {
    const payload = {
      $set: {
        $increment: prop
      }
    }
    posthog.capture('$set', payload)
  }
}

export const userSet = (props: Dict) => {
  posthog.setPersonProperties(props)
}

export const trackContentOpened = (properties: {
  entry_path: string
  content_title: string
  content_type: string[]
  content_id: number
  content_slug: string
  content_author: string
  content_publisher: string
  content_thema: string[]
  is_favorite: boolean
  user_segment: string
  user_degree: string
  first_time: boolean
  reading_type: string
  layout: string
}) => trackEvent(EVENT_CONTENT_OPENED, properties)

export const trackRelatedContentTabClicked = (properties: {
  shelf_identifier: 'YOU_MAY_LIKE' | 'SAME_AUTHOR' | 'SAME_SERIES'
  shelf_index: number
  content_title: string
  content_identifier: string
  content_type: 'book' | 'article'
}) => trackEvent(EVENT_RELATED_CONTENT_TAB_CLICKED, properties)

export const trackSearch = (properties: {
  searched_term: string
  user_segment: string
  user_degree: string
  filter_types_used?: string[]
  [key: string]: string[] | string
}) => trackEvent(EVENT_SEARCH, properties)

export const trackModalClicked = (properties: {
  path: string
  label: string
  path_redirect: string
  start_date: string
  end_date: string
  days_before_ending: number
}) => trackEvent(EVENT_MODAL_CLICKED, properties)

export const trackModalClosed = (properties: {
  path: string
  label: string
  path_redirect: string
  start_date: string
  end_date: string
  days_before_ending: number
}) => trackEvent(EVENT_MODAL_CLOSED, properties)

export const trackModalImpressions = (properties: {
  path: string
  label: string
  start_date: string
  end_date: string
  days_before_ending: number
}) => trackEvent(EVENT_MODAL_IMPRESSIONS, properties)

export const trackBannerClick = (properties: {
  position: number
  label: string
  path_redirect: string
  start_date?: string
  end_date?: string
  days_until_banner_change?: number
}) => trackEvent(EVENT_BANNER_CLICK, properties)

export const trackRunningRecordTutorialDownload = () =>
  trackEvent(EVENT_DOWNLOAD_RUNNING_RECORD_TUTORIAL, {
    content_type: 'pdf running records tutorial'
  })

export type OnboardingStartedSource =
  | 'home-prateleira'
  | 'home-continuar-lendo'
  | 'first-access'
  | 'acesso-direto'
  | 'home-sentir'

export const trackOnboardingStarted = (properties: {
  source: OnboardingStartedSource
  tutorial_state: string
}) => {
  trackEvent(EVENT_ONBOARDING_STARTED, properties)
}

export type OnboardingStepCompletedProps =
  | 'welcome'
  | 'themes'
  | 'liked-books'
  | 'shelf-recommended-books'

export const trackOnboardingStepCompleted = (properties: {
  step: OnboardingStepCompletedProps
  time_spent: number
}) => {
  trackEvent(EVENT_ONBOARDING_STEP_COMPLETED, properties)
}

export const trackOnboardingSelectedThemes = (properties: {
  themes: string[]
  count: number
}) => {
  trackEvent(EVENT_ONBOARDING_SELECTED_THEMES, properties)
}

export const trackOnboardingLikedBooks = (properties: {
  books: string[]
  count: number
}) => {
  trackEvent(EVENT_ONBOARDING_LIKED_BOOKS, properties)
}

export const trackOnboardingUnlikedBooks = (properties: {
  books: string[]
  count: number
}) => {
  trackEvent(EVENT_ONBOARDING_UNLIKED_BOOKS, properties)
}

export const trackOnboardingBookClicked = (properties: { slug: string }) =>
  trackEvent(EVENT_ONBOARDING_BOOK_CLICKED, properties)

export const trackOnboardingCompleted = (properties?: {
  source: OnboardingStartedSource
  tutorial_state: string
}) => trackEvent(EVENT_ONBOARDING_COMPLETED, properties)

export const trackRunningRecordCalculated = (properties: {
  word_count: number
  errors_count: number
  selfcorrection_count: number
  indicated_result: 'Reduz um nível' | 'Mantém no nível' | 'Sobe de nível'
  which_calculator:
    | 'calculadora isolada'
    | 'calculadora dentro do fluxo mudança de nível'
  user_id?: number
  current_level?: string
}) => {
  trackEvent(EVENT_RUNNING_RECORD_CALCULATED, properties)
}

export const trackPublisherShortcut = (properties: {
  publisher_name: string
  book_title: string
}) => {
  trackEvent(EVENT_PUBLISHER_SHORTCUT, properties)
}

export const trackAuthorShortcut = (properties: {
  author_name: string
  book_title: string
}) => {
  trackEvent(EVENT_AUTHOR_SHORTCUT, properties)
}

export const trackTimeUsageReportGuideDownloaded = () =>
  trackEvent(EVENT_TIME_USAGE_REPORT_GUIDE_DOWNLOADED, {
    content_type: 'guia de uso saudavel'
  })

export const trackTimeUsageReportRubricDownloaded = () =>
  trackEvent(EVENT_TIME_USAGE_REPORT_RUBRIC_DOWNLOADED, {
    content_type: 'pdf rubrica letramento digital'
  })

export const trackViewFullCollectionShortcut = () => {
  trackEvent(EVENT_VIEW_FULL_COLLECTION_SHORTCUT)
}

export const trackCreateActivityV3 = () => {
  trackEvent(EVENT_CREATE_ACTIVITY_V3)
}

export const trackActivityCatalogTabMyActivities = () => {
  trackEvent(EVENT_ACTIVITY_CATALOG_TAB_MY_ACTIVITIES)
}

export const trackActivityCatalogTabArvoreActivities = () => {
  trackEvent(EVENT_ACTIVITY_CATALOG_TAB_ARVORE_ACTIVITIES)
}

export const trackActivityCatalogTabAllActivities = () => {
  trackEvent(EVENT_ACTIVITY_CATALOG_TAB_ALL_ACTIVITIES)
}

export const trackActivitiesReportApplyActivity = () => {
  trackEvent(EVENT_ACTIVITIES_REPORT_APPLY_ACTIVITY)
}

export const trackActivitiesReportActivityCatalog = () => {
  trackEvent(EVENT_ACTIVITIES_REPORT_ACTIVITY_CATALOG)
}

export const trackActivitiesTabAllActivities = () => {
  trackEvent(EVENT_ACTIVITIES_TAB_ALL_ACTIVITIES)
}

export const trackActivitiesTabMyActivities = () => {
  trackEvent(EVENT_ACTIVITIES_TAB_MY_ACTIVITIES)
}

export const trackActivitiesTabArvoreActivities = () => {
  trackEvent(EVENT_ACTIVITIES_TAB_ARVORE_ACTIVITIES)
}

export const trackBookIntermediateTabActivities = () => {
  trackEvent(EVENT_BOOK_INTERMEDIATE_TAB_ACTIVITIES)
}

export const trackBookIntermediateApplyActivity = () => {
  trackEvent(EVENT_BOOK_INTERMEDIATE_APPLY_ACTIVITY)
}

export const trackBookIntermediateCreateActivity = () => {
  trackEvent(EVENT_BOOK_INTERMEDIATE_CREATE_ACTIVITY)
}

export const trackIndicationsReportView = () => {
  trackEvent(EVENT_INDICATIONS_REPORT_VIEW)
}

export const trackRunningRecordsDownloadSheet = (properties: {
  book_title: string
  book_slug: string
  book_level: string
  source: 'book_list' | 'book_modal'
}) => {
  trackEvent(EVENT_RUNNING_RECORDS_DOWNLOAD_SHEET, properties)
}

export const trackRunningRecordsCalculator = () => {
  trackEvent(EVENT_RUNNING_RECORDS_CALCULATOR)
}

export type RunningRecordsFilterType = 'unit' | 'class' | 'student'

export const trackRunningRecordsFilterChanged = (properties: {
  filter_type: RunningRecordsFilterType
  filter_value: string
  filter_id: number
}) => {
  trackEvent(EVENT_RUNNING_RECORDS_FILTER_CHANGED, properties)
}

export const trackRunningRecordsBannerClicked = () => {
  trackEvent(EVENT_RUNNING_RECORDS_BANNER_CLICKED)
}

export const trackRunningRecordsListenRecordings = (properties: {
  student_name: string
  student_id: number
  book_title: string
  book_slug: string
  recordings_count: number
}) => {
  trackEvent(EVENT_RUNNING_RECORDS_LISTEN_RECORDINGS, properties)
}

export const trackRunningRecordsPlayRecording = (properties: {
  student_name: string
  student_id: number
  book_title: string
  book_slug: string
  recording_id: string
  recording_date: string
}) => {
  trackEvent(EVENT_RUNNING_RECORDS_PLAY_RECORDING, properties)
}

export type RunningRecordsEvaluationType =
  | 'needs_support'
  | 'developing'
  | 'excellent'

export const trackRunningRecordsEvaluationSelected = (properties: {
  student_name: string
  student_id: number
  book_title: string
  book_slug: string
  recording_id: string
  evaluation: RunningRecordsEvaluationType
}) => {
  trackEvent(EVENT_RUNNING_RECORDS_EVALUATION_SELECTED, properties)
}

export const trackRunningRecordsMethodDetails = () => {
  trackEvent(EVENT_RUNNING_RECORDS_METHOD_DETAILS)
}

export const trackRunningRecordsProgressSheetDownload = () => {
  trackEvent(EVENT_RUNNING_RECORDS_PROGRESS_SHEET)
}

export const trackRunningRecordsRegisterAssessment = (properties?: {
  student_name?: string
  student_id?: number
}) => {
  trackEvent(EVENT_RUNNING_RECORDS_REGISTER_ASSESSMENT, properties)
}

export const trackRunningRecordsSaveAssessment = (properties: {
  student_name: string
  student_id: number
  level_id: number
  level_name: string
  result: string
}) => {
  trackEvent(EVENT_RUNNING_RECORDS_SAVE_ASSESSMENT, properties)
}

export const trackRunningRecordsExport = (properties: {
  export_type: 'csv' | 'xlsx' | 'pdf' | 'txt'
}) => {
  trackEvent(EVENT_RUNNING_RECORDS_EXPORT, properties)
}

export const trackRunningRecordsHistory = (properties: {
  student_name: string
  student_id: number
}) => {
  trackEvent(EVENT_RUNNING_RECORDS_HISTORY, properties)
}

export type AISuggestionStatus = 'correct' | 'partial' | 'incorrect'

export const trackAISuggestionStatusClicked = (properties: {
  exam_question_id: number
  applied_activity_id: number
  reader_id: number
  status: AISuggestionStatus
}) => {
  trackEvent(EVENT_AI_SUGGESTION_STATUS_CLICKED, properties)
}

export const trackAISuggestionAccepted = (properties: {
  exam_question_id: number
  applied_activity_id: number
  reader_id: number
}) => {
  trackEvent(EVENT_AI_SUGGESTION_ACCEPTED, properties)
}

export const trackAISuggestionIgnored = (properties: {
  exam_question_id: number
  applied_activity_id: number
  reader_id: number
}) => {
  trackEvent(EVENT_AI_SUGGESTION_IGNORED, properties)
}

export const trackAISuggestionCommentEditClicked = (properties: {
  exam_question_id: number
  applied_activity_id: number
  reader_id: number
}) => {
  trackEvent(EVENT_AI_SUGGESTION_COMMENT_EDIT_CLICKED, properties)
}

export const trackAISuggestionCommentSaved = (properties: {
  exam_question_id: number
  applied_activity_id: number
  reader_id: number
}) => {
  trackEvent(EVENT_AI_SUGGESTION_COMMENT_SAVED, properties)
}

export const trackSuperAutorShelfViewed = () => {
  trackEvent(EVENT_SUPERAUTOR_SHELF_VIEWED)
}

export const trackSuperAutorShelfCtaClicked = () => {
  trackEvent(EVENT_SUPERAUTOR_SHELF_CTA_CLICKED)
}

export const trackSuperAutorShelfBookClicked = (properties: {
  book_title: string
  book_slug: string
  book_position: number
}) => {
  trackEvent(EVENT_SUPERAUTOR_SHELF_BOOK_CLICKED, properties)
}

export const trackBookCreationBannerViewed = () => {
  trackEvent(EVENT_BOOK_CREATION_BANNER_VIEWED)
}

export const trackBookCreationBannerClicked = () => {
  trackEvent(EVENT_BOOK_CREATION_BANNER_CLICKED)
}

export const trackBookCreationModalApplyClicked = () => {
  trackEvent(EVENT_BOOK_CREATION_MODAL_APPLY_CLICKED)
}

export const trackBookCreationAdminResultsClicked = () => {
  trackEvent(EVENT_BOOK_CREATION_ADMIN_RESULTS_CLICKED)
}

export const trackPqlWriteModalViewed = () => {
  trackEvent(EVENT_PQL_WRITE_MODAL_VIEWED)
}

export const trackPqlWriteIndicateClicked = () => {
  trackEvent(EVENT_PQL_WRITE_INDICATE_CLICKED)
}

export const trackPqlWriteReadMyselfClicked = () => {
  trackEvent(EVENT_PQL_WRITE_READ_MYSELF_CLICKED)
}

export const trackPqlWriteCreateListClicked = () => {
  trackEvent(EVENT_PQL_WRITE_CREATE_LIST_CLICKED)
}
